/**
 * WorkforceOverview — attention list → Financial Pulse → 4 KPI cards → strip.
 * Requisitions removed. "Open positions" is derived. Waitlist tracked separately.
 */
import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card } from '../ui/card';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowRight, Building2, UserCheck, UserMinus,
  UserPlus, Users2, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import FinancialPulse from './FinancialPulse';
import ClinicStaffingBar from './ClinicStaffingBar';
import ActionItems from './ActionItems';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  border: '#E8E5EC', paper: '#FBF8F4',
  plum: '#4A3B5F', gold: '#B08A3A', teal: '#3B6F5D', rose: '#8A3A3A',
  ok: '#3B6F5D',
};

const SEVERITY_TONES = {
  top:    { bg: '#FEE2E2', bar: '#7C1D1D', label: 'TOP' },
  high:   { bg: '#FFF3EF', bar: '#B54B4B', label: 'HIGH' },
  medium: { bg: '#FFF8EC', bar: '#B08A3A', label: 'MED' },
  low:    { bg: '#F4F1F8', bar: '#685B72', label: 'LOW' },
};

function KpiCard({ label, value, sub, tone, testId, icon: Icon }) {
  return (
    <Card className="p-4 border text-center" style={{ borderColor: C.border }} data-testid={testId}>
      <div className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider font-semibold" style={{ color: tone || C.plum }}>
        {Icon && <Icon size={14} />} {label}
      </div>
      <div className="mt-2 text-3xl font-serif" style={{ color: C.ink }}>{value}</div>
      {sub && <div className="mt-1 text-[11px]" style={{ color: C.soft }}>{sub}</div>}
    </Card>
  );
}

function ClinicHealthCard({ health, testId }) {
  const total = (health?.green || 0) + (health?.yellow || 0) + (health?.red || 0);
  return (
    <Card className="p-4 border text-center" style={{ borderColor: C.border }} data-testid={testId}>
      <div className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider font-semibold" style={{ color: C.plum }}>
        <Building2 size={14} /> Clinic Health
      </div>
      <div className="mt-3 flex items-center justify-center gap-6">
        <div>
          <div className="text-3xl font-serif" style={{ color: C.ok }}>{health?.green || 0}</div>
          <div className="text-[10px] uppercase tracking-wider" style={{ color: C.soft }}>Green</div>
        </div>
        <div className="text-[#B08A3A]">
          <div className="text-3xl font-serif">{health?.yellow || 0}</div>
          <div className="text-[10px] uppercase tracking-wider" style={{ color: C.soft }}>Yellow</div>
        </div>
        <div className="text-[#B54B4B]">
          <div className="text-3xl font-serif">{health?.red || 0}</div>
          <div className="text-[10px] uppercase tracking-wider" style={{ color: C.soft }}>Red</div>
        </div>
      </div>
      <div className="mt-2 text-[11px]" style={{ color: C.soft }}>
        of {total} clinics
        {health?.od_vacant > 0 && (
          <span className="ml-2 font-semibold" style={{ color: '#B54B4B' }}>· {health.od_vacant} OD vacant</span>
        )}
      </div>
    </Card>
  );
}

function AttritionCard({ attrition, testId }) {
  return (
    <Card className="p-4 border text-center" style={{ borderColor: C.border }} data-testid={testId}>
      <div className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider font-semibold" style={{ color: C.plum }}>
        <UserMinus size={14} /> 90-Day Attrition
      </div>
      <div className="mt-2 text-3xl font-serif" style={{ color: C.ink }}>{attrition?.total ?? 0}</div>
      <div className="mt-1 text-[11px]" style={{ color: C.soft }}>
        <span style={{ color: C.rose }}>{attrition?.voluntary || 0} vol</span> · <span style={{ color: '#B54B4B' }}>{attrition?.involuntary || 0} invol</span> · <span>{attrition?.planned_end || 0} planned</span>
      </div>
    </Card>
  );
}

function AttentionList({ items, onRankChange }) {
  if (!items || !items.length) {
    return (
      <Card className="p-4 border text-sm text-center" style={{ borderColor: C.border, color: C.soft }} data-testid="hr-attention-empty">
        All caught up — no items need HR attention right now.
      </Card>
    );
  }
  return (
    <Card className="border overflow-hidden" style={{ borderColor: C.border }} data-testid="hr-attention-list">
      <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: C.border, background: C.paper }}>
        <AlertTriangle size={16} style={{ color: '#B54B4B' }} />
        <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: C.ink2 }}>
          Attention · {items.length} item{items.length === 1 ? '' : 's'}
        </div>
        <div className="ml-auto text-[11px]" style={{ color: C.soft }}>Rank to pin · else severity</div>
      </div>
      <div className="divide-y" style={{ borderColor: C.border }}>
        {items.map((it, i) => {
          const tone = SEVERITY_TONES[it.severity] || SEVERITY_TONES.low;
          return (
            <div key={it.key || i} className="flex items-stretch hover:bg-[#FBF8F4] transition-colors"
                 data-testid={`hr-attention-item-${it.kind}`}>
              <div style={{ background: tone.bar, width: 4 }} />
              {/* Numeric rank pin — HR override */}
              <div className="pl-3 pr-1 py-3 flex items-center" onClick={(e) => e.stopPropagation()}>
                <input
                  type="number"
                  min="0"
                  placeholder="—"
                  defaultValue={it.override_rank ?? ''}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    const next = v === '' ? null : Math.max(0, parseInt(v, 10) || 0);
                    if ((next ?? null) !== (it.override_rank ?? null)) {
                      onRankChange(it.key, next);
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  className="w-11 h-7 text-center text-xs border rounded"
                  style={{ borderColor: C.border, color: C.ink }}
                  title="Pin this item to a numeric priority (blank = auto)"
                  data-testid={`hr-attention-rank-${it.key}`}
                />
              </div>
              <Link to={it.link || '/hr'}
                    className="flex-1 px-2 py-3 flex items-center gap-3">
                <div className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded"
                     style={{ background: tone.bg, color: tone.bar }}>{tone.label}</div>
                {it.auto && (
                  <div className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded"
                       style={{ background: '#F4F1F8', color: C.plum }}
                       data-testid={`hr-attention-auto-${it.key}`}>AUTO</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate" style={{ color: C.ink }}>{it.label}</div>
                  <div className="text-[11px] truncate" style={{ color: C.soft }}>{it.detail}</div>
                </div>
                <ArrowRight size={14} style={{ color: C.soft }} />
              </Link>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function IncomingDepartingStrip({ incoming, departing, byStage }) {
  return (
    <div className="grid md:grid-cols-3 gap-3">
      <Card className="p-4 border text-center" style={{ borderColor: C.border }} data-testid="hr-incoming-30d">
        <div className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider font-semibold" style={{ color: C.ok }}>
          <UserPlus size={14} /> Incoming · 30 days
        </div>
        <div className="mt-2 text-3xl font-serif" style={{ color: C.ink }}>{incoming?.length || 0}</div>
        <div className="mt-2 space-y-1">
          {(incoming || []).slice(0, 4).map((p, i) => (
            <div key={i} className="text-[11px]" style={{ color: C.ink2 }}>
              {p.name} · {p.clinic || '—'} · <span style={{ color: C.soft }}>{p.start_date}</span>
            </div>
          ))}
          {(!incoming || !incoming.length) && <div className="text-[11px]" style={{ color: C.soft }}>—</div>}
        </div>
      </Card>
      <Card className="p-4 border text-center" style={{ borderColor: C.border }} data-testid="hr-departing-30d">
        <div className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider font-semibold" style={{ color: C.rose }}>
          <UserMinus size={14} /> Departing · 30 days
        </div>
        <div className="mt-2 text-3xl font-serif" style={{ color: C.ink }}>{departing?.length || 0}</div>
        <div className="mt-2 space-y-1">
          {(departing || []).slice(0, 4).map((p, i) => (
            <div key={i} className="text-[11px]" style={{ color: C.ink2 }}>
              {p.name} · {p.clinic || '—'} · <span style={{ color: C.soft }}>{p.last_day}</span>
            </div>
          ))}
          {(!departing || !departing.length) && <div className="text-[11px]" style={{ color: C.soft }}>—</div>}
        </div>
      </Card>
      <Card className="p-4 border text-center" style={{ borderColor: C.border }} data-testid="hr-pipeline-by-stage">
        <div className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider font-semibold" style={{ color: C.plum }}>
          <UserCheck size={14} /> Active pipeline
        </div>
        <div className="mt-2 text-3xl font-serif" style={{ color: C.ink }}>
          {Object.values(byStage || {}).reduce((a, b) => a + b, 0)}
        </div>
        <div className="mt-2 space-y-1 max-h-28 overflow-auto text-left inline-block">
          {Object.entries(byStage || {}).map(([stage, n]) => (
            <div key={stage} className="text-[11px] flex justify-between gap-4" style={{ color: C.ink2 }}>
              <span className="truncate">{stage}</span><span className="font-semibold">{n}</span>
            </div>
          ))}
          {!Object.keys(byStage || {}).length && <div className="text-[11px]" style={{ color: C.soft }}>No active candidates</div>}
        </div>
      </Card>
    </div>
  );
}

export default function WorkforceOverview() {
  const [data, setData] = useState(null);
  const [staffing, setStaffing] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => Promise.all([
    api.get('/hr/overview'),
    api.get('/hr/staffing'),
  ]).then(([o, s]) => { setData(o.data); setStaffing(s.data); })
    .catch(() => toast.error('Could not load HR overview'))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const setRank = async (key, rank) => {
    try {
      await api.post('/hr/attention/rank', { key, rank });
      toast.success(rank == null ? 'Priority cleared' : `Pinned as #${rank}`);
      load();
    } catch { toast.error('Could not update priority'); }
  };

  if (loading) return <div className="p-8 text-center text-sm" style={{ color: C.soft }}>Loading CEO staffing overview…</div>;
  if (!data) return null;

  const k = data.kpis || {};
  const active = k.active_headcount || {};

  return (
    <div className="space-y-4" data-testid="hr-workforce-overview">
      {/* 1. Attention list */}
      <AttentionList items={data.attention} onRankChange={setRank} />

      {/* 2. Clinic staffing visual (bar chart, lowest-first) */}
      <ClinicStaffingBar rows={staffing?.rows || []} />

      {/* 3. Financial Pulse */}
      <FinancialPulse data={data.financial_pulse} />

      {/* 3. Four KPI cards (Weekly Sessions at Risk removed per user directive) */}
      <div className="grid md:grid-cols-4 gap-3">
        <KpiCard
          testId="hr-kpi-active-headcount"
          icon={Users2}
          label="Active Headcount"
          value={active.total ?? 0}
          sub={`${active.ft || 0} FT · ${active.pt || 0} PT · ${active.prn || 0} PRN · ${active.extern || 0} Extern`}
          tone={C.plum}
        />
        <KpiCard
          testId="hr-kpi-open-positions"
          icon={UserPlus}
          label="Positions to Hire"
          value={k.open_positions ?? 0}
          sub={`Demand-driven (waitlist × gap) · ${k.positions_capacity || 0} total capacity · ${k.waitlist_total || 0} client${k.waitlist_total === 1 ? '' : 's'} waiting`}
          tone={C.teal}
        />
        <ClinicHealthCard testId="hr-kpi-clinic-health" health={k.clinic_health} />
        <AttritionCard testId="hr-kpi-attrition-90d" attrition={k.attrition_90d} />
      </div>

      {/* 4. Incoming / Departing / Active-pipeline strip */}
      <IncomingDepartingStrip
        incoming={data.incoming_30d}
        departing={data.departing_30d}
        byStage={data.active_candidates_by_stage}
      />

      <div className="text-[11px] text-center" style={{ color: C.soft }} data-testid="hr-median-tenure">
        Median tenure of active staff: <span className="font-semibold" style={{ color: C.ink2 }}>
          {k.median_tenure_days == null ? '—' : `${Math.round(k.median_tenure_days / 30.4)} months`}
        </span>
      </div>

      {/* 5. Standup follow-ups / action items */}
      <ActionItems />
    </div>
  );
}
